import { Link } from "@tanstack/react-router";
import {
  Activity,
  Announcement03,
  Award03,
  Colors,
  Fingerprint04,
  Globe05,
  Heart,
  ImageIndentLeft,
  LineChartUp02,
  Link01,
  Share03,
  User01,
  UserCircle,
  Users02,
  VideoRecorder,
  XClose,
  // XCircle, // Commented out - Remove from Collab feature pending Samples API integration
} from "@untitledui/icons";
import { Play } from "lucide-react";
import { useState } from "react";
import { useCandidate } from "@/api/candidates";
import { GroupedActionMenu } from "@/components/action-menu/grouped-action-menu";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { Badge } from "@/components/base/badges/badges";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { NetworkSection } from "@/components/creator-network/creator-network";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
// import { RemoveCreatorFromCollabModal } from "@/components/remove-creator-from-collab-modal"; // Commented out - Remove from Collab feature pending Samples API integration
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useCurrency } from "@/hooks/use-currency";
import { useMerchantFeatures } from "@/hooks/use-merchant-features";
import { buildCandidateActionGroups } from "@/lib/creator-action-groups";
import { AddToCandidateListsModal } from "@/pages/candidate-lists/add-to-candidate-lists-modal";
import { useMerchant } from "@/providers/MerchantProvider";
import { candidateExplorerRoute } from "@/router";
import { formatDate } from "@/utils/date-conversion";
import { formatCreatorId } from "@/utils/formatters";
import { CandidateClassificationCard } from "./candidate-classification-card";
import { ActivityTab } from "./tabs/activity-tab";

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const StarRating = ({ rating }: { rating: number | null }) => {
  if (rating === null || rating === undefined) {
    return <span className="text-tertiary">-</span>;
  }
  return <span>{rating.toFixed(1)}/ 5</span>;
};
const formatContentFeature = (str: string) =>
  str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

type Tab = "about" | "performance" | "videos" | "activity";

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2 text-sm font-medium text-secondary pb-2 border-b border-border-secondary">
    {children}
  </div>
);

const Inner = ({ id, onClose }: { id?: string; onClose: () => void }) => {
  const [activeTab, setActiveTab] = useState<Tab>("about");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [openDialog, setOpenDialog] = useState<"send-message">();
  const [addToListsOpen, setAddToListsOpen] = useState(false);
  // const [showRemoveCollabModal, setShowRemoveCollabModal] = useState(false); // Commented out - Remove from Collab feature pending Samples API integration
  const { data } = useCandidate(id);
  const features = useMerchantFeatures();
  const hasAffiliateAuthorisation = !features.includes("noAffiliateAuthorisation");
  const currency = useCurrency();
  const merchantId = useMerchant();
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (!data)
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );

  const topCategories = data.categoryGmvDistribution
    ?.filter((c) => c.category !== "-1")
    .slice(0, 3)
    .map((c) => c.category);

  const topGender = data.followerGenderDistribution?.reduce(
    (max, curr) => (Number.parseFloat(curr.percentage) > Number.parseFloat(max.percentage) ? curr : max),
    data.followerGenderDistribution[0],
  );

  const topAgeRange = data.followerAgeDistribution?.reduce(
    (max, curr) => (Number.parseFloat(curr.percentage) > Number.parseFloat(max.percentage) ? curr : max),
    data.followerAgeDistribution[0],
  );

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto px-[48px] py-[48px]">
      {/* Header */}
      <div className="p-0">
        <div className="flex gap-5 items-start justify-between">
          <div className="flex gap-5 items-start">
            {data.avatar ? (
              <img
                src={data.avatar}
                alt={data.nickname ?? data.username}
                className="w-24 h-24 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-medium text-primary">
                  {(data.nickname ?? data.username)?.charAt(0).toUpperCase() ?? "?"}
                </span>
              </div>
            )}
            <div className="flex flex-col gap-3 min-w-0">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-primary">{data.nickname ?? data.username}</h2>
                  {data.invitations && data.invitations.length > 0 && (
                    <Badge color="orange">Invited To Collaboration</Badge>
                  )}
                </div>
                <p className="text-sm text-tertiary">
                  {data.categories?.slice(0, 2).join(" | ") || formatCreatorId(data.username)}
                </p>
              </div>
              <div className="flex gap-6 items-center">
                <div className="flex flex-col">
                  <span className="text-base font-semibold text-primary">
                    {currencyFormatter.format(data.gmvAmount ?? 0)}
                  </span>
                  <span className="text-xs text-tertiary">GMV</span>
                </div>
                <div className="h-8 w-px bg-border-secondary" />
                <div className="flex flex-col">
                  <span className="text-base font-semibold text-primary">{data.ecVideoCount ?? 0} videos</span>
                  <span className="text-xs text-tertiary">E-commerce</span>
                </div>
                <div className="h-8 w-px bg-border-secondary" />
                <div className="flex flex-col">
                  <span className="text-base font-semibold text-primary">
                    <StarRating rating={data.sellerRating} />
                  </span>
                  <span className="text-xs text-tertiary">Seller Rating</span>
                </div>
              </div>
            </div>
          </div>
          {/* Actions Dropdown & Close Button */}
          <div className="flex items-center gap-2">
            {hasAffiliateAuthorisation && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Dropdown.DotsButton className="flex items-center justify-center p-2 rounded-lg border border-transparent hover:border-border-primary active:border-border-primary data-[state=open]:border-border-primary" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <GroupedActionMenu
                    groups={buildCandidateActionGroups({
                      onInvite: () => setShowInviteModal(true),
                      onMessage: () => setOpenDialog("send-message"),
                      onAddToList: () => setAddToListsOpen(true),
                    })}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center p-2 rounded-lg cursor-pointer text-fg-quaternary hover:text-fg-quaternary_hover border border-transparent hover:border-border-primary active:border-border-primary transition duration-100 ease-linear"
              aria-label="Close"
            >
              <XClose className="size-5" />
            </button>
          </div>
        </div>

        {/* Classification Card */}
        {data.classification && (
          <div className="pt-6">
            <CandidateClassificationCard
              classification={data.classification}
              topCategory={data.matchingCategory ?? undefined}
              ecVideoCount={data.ecVideoCount ?? 0}
              gmvAmount={data.gmvAmount ?? 0}
              currencyFormatter={currencyFormatter}
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as Tab)} className="w-full pt-8">
          <Tabs.List
            type="underline"
            items={[
              { id: "about" as const, label: "About", icon: <User01 className="w-4 h-4" /> },
              {
                id: "performance" as const,
                label: "Performance",
                icon: <LineChartUp02 className="w-4 h-4" />,
              },
              { id: "videos" as const, label: "Videos", icon: <VideoRecorder className="w-4 h-4" /> },
              { id: "activity" as const, label: "Activity", icon: <Activity className="w-4 h-4" /> },
            ]}
          >
            {(tab) => <Tabs.Item {...tab} className="w-full" />}
          </Tabs.List>
        </Tabs>
      </div>

      {/* Tab Content */}
      <div className={activeTab === "activity" ? "h-full shrink-0 p-0" : "flex-1 p-0 pt-12"}>
        {activeTab === "about" && (
          <div className="flex flex-row gap-x-8 gap-y-6 h-full">
            {/* Left Column */}
            <div className="flex flex-col gap-12 grow">
              {data.aiFeatures?.descriptiveSummaryOfCreator ||
              (data.aiFeatures?.toneAndApproach && data.aiFeatures.toneAndApproach.length > 0) ||
              (data.aiFeatures?.contentFocus && data.aiFeatures.contentFocus.length > 0) ||
              (data.aiFeatures?.productionQuality && data.aiFeatures.productionQuality.length > 0) ||
              (data.aiFeatures?.visualAesthetic && data.aiFeatures.visualAesthetic.length > 0) ? (
                <>
                  {/* Descriptive Summary */}
                  {data.aiFeatures?.descriptiveSummaryOfCreator && (
                    <div className="flex flex-col gap-3">
                      <SectionHeader>
                        <UserCircle size={16} />
                        Descriptive Summary of Creator
                      </SectionHeader>
                      <p className="text-sm text-secondary">{data.aiFeatures.descriptiveSummaryOfCreator}</p>
                    </div>
                  )}

                  {/* Tone & Approach */}
                  {data.aiFeatures?.toneAndApproach && data.aiFeatures.toneAndApproach.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <SectionHeader>
                        <Announcement03 size={16} /> Tone & Approach
                      </SectionHeader>
                      <div className="flex flex-wrap gap-2">
                        {data.aiFeatures.toneAndApproach.map((item) => (
                          <Badge key={item} color="gray">
                            {formatContentFeature(item)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Content Focus */}
                  {data.aiFeatures?.contentFocus && data.aiFeatures.contentFocus.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <SectionHeader>
                        <ImageIndentLeft size={16} />
                        Content Focus
                      </SectionHeader>
                      <div className="flex flex-wrap gap-2">
                        {data.aiFeatures.contentFocus.map((item) => (
                          <Badge key={item} color="gray">
                            {formatContentFeature(item)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Production Quality */}
                  {data.aiFeatures?.productionQuality && data.aiFeatures.productionQuality.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <SectionHeader>
                        <Award03 size={16} />
                        Production Quality
                      </SectionHeader>
                      <div className="flex flex-wrap gap-2">
                        {data.aiFeatures.productionQuality.map((item) => (
                          <Badge key={item} color="gray">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Visual Aesthetic */}
                  {data.aiFeatures?.visualAesthetic && data.aiFeatures.visualAesthetic.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <SectionHeader>
                        <Fingerprint04 size={16} />
                        Visual aesthetic
                      </SectionHeader>
                      <div className="flex flex-wrap gap-2">
                        {data.aiFeatures.visualAesthetic.map((item) => (
                          <Badge key={item} color="gray">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="relative flex flex-col justify-center gap-2 p-8 rounded-xl overflow-hidden grow">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-100/50 via-transparent to-pink-100/50" />
                  <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-orange-200/30 to-transparent rounded-full blur-2xl" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-pink-200/30 to-transparent rounded-full blur-2xl" />
                  <div className="relative">
                    <h3 className="text-lg font-semibold text-primary">Creator insights unavailable</h3>
                    <p className="text-sm text-secondary max-w-sm mt-2">
                      We couldn't generate a summary for this creator. This usually happens if the source videos are no
                      longer available or if there was a temporary system error.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-12 shrink min-w-54">
              <NetworkSection network={data.network} />

              {/* Top selling categories */}
              {topCategories && topCategories.length > 0 && (
                <div className="flex flex-col gap-3">
                  <SectionHeader>
                    <Colors size={16} />
                    Top {topCategories.length} selling categories
                  </SectionHeader>
                  <div className="flex flex-wrap gap-2">
                    {topCategories.map((category) =>
                      merchantId ? (
                        <Link
                          key={category}
                          to={candidateExplorerRoute.to}
                          params={{ merchantId }}
                          search={{ topSellingCategories: [{ id: category, label: category }] }}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-fit"
                          aria-label={`Browse candidates in ${category}`}
                        >
                          <Badge
                            color="gray"
                            className="cursor-pointer hover:ring-2 hover:ring-brand-primary transition-all"
                          >
                            {category}
                          </Badge>
                        </Link>
                      ) : (
                        <Badge key={category} color="gray">
                          {category}
                        </Badge>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Demographics */}
              {(topGender || topAgeRange) && (
                <div className="flex flex-col gap-3">
                  <SectionHeader>
                    <Globe05 size={16} />
                    Demographics
                  </SectionHeader>
                  <div className="flex flex-col">
                    {topGender && (
                      <div className="flex justify-between py-2 border-b border-border-secondary">
                        <span className="text-sm text-secondary capitalize">{topGender.gender}</span>
                        <span className="text-sm text-secondary">
                          {percentFormatter.format(Number.parseFloat(topGender.percentage))}
                        </span>
                      </div>
                    )}
                    {topAgeRange && (
                      <div className="flex justify-between py-2 border-b border-border-secondary">
                        <span className="text-sm text-secondary">Age {topAgeRange.age}</span>
                        <span className="text-sm text-secondary">
                          {percentFormatter.format(Number.parseFloat(topAgeRange.percentage))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 py-1 border-b border-border-secondary">
                  <Users02 size={16} />
                  <span className="text-base font-medium text-primary">
                    {data.followerCount ? numFormatter.format(data.followerCount) : "-"}
                  </span>
                  <span className="text-sm text-tertiary">Followers</span>
                </div>
                <div className="flex items-center gap-2 py-1">
                  <Heart size={16} />
                  <span className="text-base font-medium text-primary">
                    {data.avgEcVideoLikeCount ? numFormatter.format(data.avgEcVideoLikeCount) : "-"}
                  </span>
                  <span className="text-sm text-tertiary">Avg. Likes</span>
                </div>
              </div>

              {/* Links */}
              {(data.profilePage || (data.externalUrls && data.externalUrls.length > 0)) && (
                <div className="flex flex-col gap-3">
                  <SectionHeader>
                    <Share03 size={16} />
                    Links
                  </SectionHeader>
                  <div className="flex flex-col gap-2">
                    {data.profilePage && (
                      <a
                        href={data.profilePage}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-secondary hover:text-primary hover:underline flex items-center gap-1"
                      >
                        See TikTok profile
                        <span className="text-xs">↗</span>
                      </a>
                    )}
                    {data.externalUrls?.map((url) => (
                      <a
                        key={url}
                        href={url.startsWith("http") ? url : `https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-secondary hover:text-primary hover:underline flex items-center gap-1"
                      >
                        {url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                        <span className="text-xs">↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Invited Collaborations */}
              {data.invitations && data.invitations.length > 0 && (
                <div className="flex flex-col gap-3">
                  <SectionHeader>
                    <Link01 size={16} />
                    Invited collaborations
                  </SectionHeader>
                  <div className="flex flex-col gap-2">
                    {data.invitations.map((invitation) => (
                      <div key={invitation.collaborationId} className="flex items-center justify-between py-1">
                        <a
                          href={`https://affiliate-us.tiktok.com/connection/target-invitation/detail?invitation_id=${invitation.collaborationId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-secondary hover:text-primary hover:underline flex items-center gap-1"
                        >
                          {invitation.collaborationName}
                          <span className="text-xs">↗</span>
                        </a>
                        <Badge color="orange" size="sm">
                          Invited
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "performance" && (
          <div className="flex flex-col gap-6">
            {/* GMV Breakdown */}
            <div className="flex flex-col gap-3">
              <SectionHeader>GMV Breakdown</SectionHeader>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col p-4 bg-secondary rounded-lg">
                  <span className="text-xl font-semibold text-primary">
                    {currencyFormatter.format(data.gmvAmount ?? 0)}
                  </span>
                  <span className="text-sm text-tertiary">Total GMV</span>
                </div>
                <div className="flex flex-col p-4 bg-secondary rounded-lg">
                  <span className="text-xl font-semibold text-primary">
                    {currencyFormatter.format(data.liveGmvAmount ?? 0)}
                  </span>
                  <span className="text-sm text-tertiary">Live GMV</span>
                </div>
                <div className="flex flex-col p-4 bg-secondary rounded-lg">
                  <span className="text-xl font-semibold text-primary">
                    {currencyFormatter.format(data.videoGmvAmount ?? 0)}
                  </span>
                  <span className="text-sm text-tertiary">Video GMV</span>
                </div>
              </div>
            </div>

            {/* Seller Rating & Stats */}
            <div className="grid grid-cols-2 gap-4 mt-10">
              {/* Seller Rating Card */}
              <div className="flex flex-col p-4 bg-secondary rounded-lg gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-primary">Seller Rating</span>
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="#F59E0B" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-sm font-semibold text-primary">
                      {data.sellerRating?.toFixed(1) ?? "-"} / 5.0
                    </span>
                  </div>
                </div>
                <span className="text-xs text-tertiary">From sellers who worked with this creator</span>
              </div>

              {/* Commission & Products */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between p-3 border-b border-border-secondary">
                  <span className="text-sm text-secondary">Average Commission rate</span>
                  <span className="text-sm font-medium text-primary">
                    {data.avgCommissionRate ? percentFormatter.format(data.avgCommissionRate) : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3">
                  <span className="text-sm text-secondary">Number of promoted products</span>
                  <span className="text-sm font-medium text-primary">
                    {numFormatter.format(data.promotedProductNum ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "videos" && (
          <div className="flex flex-col gap-4">
            <SectionHeader>Recent content</SectionHeader>
            {data.aiFeatures?.videos && data.aiFeatures.videos.length > 0 ? (
              <>
                <div className="flex flex-col gap-4">
                  {data.aiFeatures.videos.map((video) => {
                    const date = new Date(video.videoTimestamp * 1000);
                    const formattedDate = formatDate(date);
                    const formattedTime = date.toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                    return (
                      <a
                        key={video.videoUrl}
                        href={video.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex rounded-lg border border-primary overflow-hidden hover:bg-secondary transition-colors"
                      >
                        {/* Thumbnail */}
                        <div className="relative w-32 h-48 flex-shrink-0 bg-gray-100 rounded-l-lg overflow-hidden">
                          {video.thumbnail ? (
                            <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-200">
                              <span className="text-xs text-tertiary">No thumbnail</span>
                            </div>
                          )}
                          {/* Play button overlay - matching brand creator style */}
                          <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm rounded p-1.5 pointer-events-none">
                            <Play className="w-4 h-4 text-gray-700 fill-gray-700" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex flex-col flex-1 min-w-0 p-3">
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="text-sm font-medium text-primary line-clamp-1">{video.title}</h4>
                            </div>
                            <span className="text-xs text-tertiary mt-0.5">
                              {formattedDate} @{formattedTime}
                            </span>
                          </div>
                          <div className="border-t border-primary my-2" />
                          <div className="flex gap-6 p-4">
                            <div className="flex flex-col">
                              <span className="text-xs text-tertiary">Views</span>
                              <span className="text-sm font-medium text-primary">
                                {numFormatter.format(video.viewCount)}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-xs text-tertiary">Likes</span>
                              <span className="text-sm font-medium text-primary">
                                {numFormatter.format(video.likeCount)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
                {/* Link at bottom of list */}
                {data.profilePage && (
                  <a
                    href={data.profilePage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-secondary hover:text-primary hover:underline flex items-center gap-1"
                  >
                    See all content from this creator
                    <span className="text-xs">↗</span>
                  </a>
                )}
              </>
            ) : (
              <div className="text-sm text-tertiary">No videos available</div>
            )}
          </div>
        )}

        {activeTab === "activity" && (
          <ActivityTab
            data={{
              creatorId: data.tikTokCreatorOpenId,
              creatorName: data.username,
              creatorAvatar: data.avatar,
            }}
            profileType="candidate"
          />
        )}
      </div>

      {/* Invite to Collaboration Modal */}
      {data && hasAffiliateAuthorisation && (
        <InviteToCollaborationModal
          creator={{
            openId: data.tikTokCreatorOpenId,
            username: data.username,
            nickname: data.nickname,
            avatar: data.avatar,
          }}
          open={showInviteModal}
          onOpenChange={setShowInviteModal}
        />
      )}

      {/* Message Dialog */}
      {data && (
        <MessageCreatorsDialog
          recipients={[
            {
              creatorId: data.username,
              handle: data.nickname ?? data.username ?? null,
              thumbnail: data.avatar,
            },
          ]}
          open={openDialog === "send-message"}
          onOpenChange={(open) => setOpenDialog(open ? "send-message" : undefined)}
        />
      )}

      {/* Add to Lists Dialog */}
      {id && (
        <AddToCandidateListsModal
          selectedCandidates={[id]}
          onAddToList={() => setAddToListsOpen(false)}
          open={addToListsOpen}
          onOpenChange={setAddToListsOpen}
        />
      )}

      {/* Remove from Collaboration Modal - commented out - pending Samples API integration
      {data && (
        <RemoveCreatorFromCollabModal
          creator={{
            creatorId: data.username,
            creatorName: data.nickname || data.username,
            thumbnail: data.avatar,
            targetCollaborations: data.invitations?.map((inv) => ({
              collaborationId: inv.collaborationId,
              name: inv.collaborationName || "Unnamed Collaboration",
              status: "NORMAL",
            })),
          }}
          open={showRemoveCollabModal}
          onOpenChange={setShowRemoveCollabModal}
        />
      )}
      */}
    </div>
  );
};

type CandidateDetailsProps = {
  id?: string;
  onClose: () => void;
  embedded?: boolean;
};

export const CandidateDetails = ({ id, onClose, embedded = false }: CandidateDetailsProps) => {
  if (!id) return null;

  if (embedded) {
    return <Inner id={id} onClose={onClose} />;
  }

  return (
    <Sheet open={!!id} onOpenChange={onClose}>
      <SheetContent className="w-[900px] sm:max-w-[900px] p-0 flex flex-col" hideCloseButton>
        <Inner id={id} onClose={onClose} />
      </SheetContent>
    </Sheet>
  );
};
